import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import Person2OutlinedIcon from '@mui/icons-material/Person2Outlined';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import React, { SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import NotificationItem from '../../app/admin/notifications/components/NotificationItem';
import { AdminNotificationService } from '../../app/admin/notifications/service';
import authSlice from '../../store/slices/auth.slice';
import { RootState } from '../../store/store';
import { EModules } from '../enums/modules/EModules';
import { ERole } from '../enums/user/ERole';
import { ETheme } from '../enums/user/ETheme';
import { getRGBAColor } from '../functions/getRGBAColor';
import { handleError } from '../functions/handleError';
import { handleNotificationClick } from '../functions/handleNotificationClick';
import { usePagination } from '../hooks/usePagination';
import { INotifications } from '../interfaces/notifications/INotifications';
import { ILayout } from '../interfaces/shared/ILayout';
import { IUser } from '../interfaces/user/IUser';
import { darkPalette, lightPalette } from '../mui/palettes';
import { darkTheme, lightTheme } from '../mui/theme';

interface IProps {
  isCollapsed: boolean;
  routes: ILayout[] | undefined;

  setIsCollapsed: React.Dispatch<SetStateAction<boolean>>;
}

const Header = ({ isCollapsed, setIsCollapsed, routes }: IProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const modules = useSelector((state: RootState) => state.module?.list);
  const authState = useSelector((state: RootState) => state.auth);
  const { userData } = useSelector((state: RootState) => state.auth);
  const company = useSelector((state: RootState) => state?.companySettings);
  const isNormalUser = userData?.role === ERole.USER;
  const name = userData?.firstName + ' ' + userData?.lastName;
  const dispatch = useDispatch();
  const isNotificationFeatureEnabled =
    modules?.some(
      (module) =>
        (module.name as unknown as EModules) === EModules.NOTIFICATION &&
        module.isEnabled,
    ) || userData?.role === ERole.DEVELOPER;
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null,
  );
  const { pagination, setPagination } = usePagination({ perPage: 20 });
  const [notifications, setNotifications] = useState<INotifications[]>([]);
  const [toggleNotifications, setToggleNotifications] =
    React.useState<null | HTMLElement>(null);
  const correctPalette =
    userData?.theme === ETheme.LIGHT ? lightPalette : darkPalette;
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };
  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };
  const handleOpenNotificationsMenu = (
    event: React.MouseEvent<HTMLElement>,
  ) => {
    setToggleNotifications(event.currentTarget);
  };
  const handleCloseNotificationsMenu = () => {
    setToggleNotifications(null);
  };

  const logoutUser = () => {
    localStorage.clear();
    navigate('/');
    dispatch(authSlice.actions.setLogoutState());
  };
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md')); // Hides sidebar on md and below

  const clearNotifications = () => {
    AdminNotificationService.clearAllNotifications()
      .then(() => {
        setNotifications((prev) =>
          prev.map((notification) => ({ ...notification, isSeen: true })),
        );
        dispatch(
          authSlice.actions.setLoginState({
            ...authState,
            userData: {
              ...authState.userData,
              unreadNotifications: 0,
            },
          }),
        );
      })
      .catch((err) => handleError(err));
  };

  useEffect(() => {
    isNotificationFeatureEnabled &&
      AdminNotificationService.getAll(pagination)
        .then((response) => {
          setNotifications(response?.data);
        })
        .catch((err) => handleError(err));
  }, [pagination, isNotificationFeatureEnabled]);
  return (
    <header className={'mb-md-4'}>
      {isMobileOrTablet ? (
        <Box
          onClick={() => {
            setIsCollapsed(!isCollapsed);
            localStorage.setItem('menuCollapsed', String(!isCollapsed));
          }}
        >
          <MenuIcon fontSize="large" />
          <Drawer open={isCollapsed} onClose={() => setIsCollapsed(false)}>
            <Box
              sx={{
                width: 250,
                height: '100%',
                backgroundColor:
                  userData?.theme === ETheme.LIGHT
                    ? lightTheme.palette.background.default
                    : darkTheme.palette.background.default,
              }}
              role="presentation"
              onClick={() => setIsCollapsed(false)}
            >
              <img
                src={
                  company?.thumbnail
                    ? userData?.theme === ETheme.DARK
                      ? company?.invertedThumbnail
                      : company?.thumbnail
                    : userData?.theme === 'light'
                      ? '/logo-light.png'
                      : '/logo-dark.svg'
                }
                alt="Logo"
                width="50%"
                style={{ padding: '1rem' }}
              />
              <List>
                {routes?.map(({ title, path, icon }) => {
                  const isActive = pathname?.includes(path);
                  return (
                    <ListItem key={title} disablePadding>
                      <ListItemButton onClick={() => navigate(path)}>
                        <Typography
                          sx={{
                            color: isActive ? 'var(--primaryColor)' : '',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <span style={{ marginRight: 10, display: 'flex' }}>
                            {icon}
                          </span>
                          {t(title)}
                        </Typography>
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          </Drawer>
        </Box>
      ) : (
        <h2>
          {t('general.login.welcome')}, {name} 👋
        </h2>
      )}
      <div className={'d-flex align-items-center'}>
        {isNotificationFeatureEnabled && (
          <Box sx={{ marginRight: '20px', cursor: 'pointer' }}>
            <Card
              sx={{ padding: '18px', display: 'flex' }}
              onClick={handleOpenNotificationsMenu}
            >
              {userData && userData?.unreadNotifications > 0 ? (
                <Badge
                  badgeContent={userData.unreadNotifications}
                  color="primary"
                  sx={{
                    '& .MuiBadge-badge': {
                      color: lightPalette.secondary.main,
                    },
                  }}
                >
                  <NotificationsActiveOutlinedIcon />
                </Badge>
              ) : (
                <NotificationsActiveOutlinedIcon />
              )}
            </Card>
            <Menu
              sx={{ maxHeight: 'unset', height: '350px', mt: '50px' }}
              id="menu-appbar"
              anchorEl={toggleNotifications}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(toggleNotifications)}
              onClose={handleCloseNotificationsMenu}
            >
              {notifications?.map((notification) => {
                return (
                  <MenuItem
                    key={notification?.id}
                    sx={{
                      borderBottom: `1px solid ${getRGBAColor('--primaryColor', 0.08)}`,
                      background: notification.isSeen
                        ? correctPalette.background.default
                        : getRGBAColor('--primaryColor', 0.08),
                    }}
                    onClick={async () => {
                      setAnchorElUser(null);
                      await handleNotificationClick(
                        notification,
                        userData as IUser,
                        navigate,
                        setPagination,
                        dispatch,
                        authState,
                      );
                    }}
                  >
                    <NotificationItem
                      notification={notification}
                      correctPalette={correctPalette}
                    />
                  </MenuItem>
                );
              })}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '6px 16px',
                  background: correctPalette.background.default,
                  position: 'sticky',
                  bottom: 0,
                  right: 0,
                }}
              >
                <Button
                  type={'button'}
                  variant={'text'}
                  color={'primary'}
                  onClick={clearNotifications}
                >
                  {t('general.clearAll')}
                </Button>
                <Button
                  type={'button'}
                  variant={'text'}
                  color={'primary'}
                  onClick={() =>
                    navigate(
                      `/${isNormalUser ? 'user' : 'admin'}/notifications`,
                    )
                  }
                >
                  {t('general.seeAllNotifications')}
                </Button>
              </Box>
            </Menu>
          </Box>
        )}
        <Card sx={{ padding: '10px', cursor: 'pointer' }}>
          <Box sx={{ display: 'flex' }} onClick={handleOpenUserMenu}>
            <IconButton sx={{ p: 0, marginRight: '0.5rem' }}>
              <Avatar alt={name} src={userData?.profilePhoto?.thumbnail} />
            </IconButton>
            <div>
              <h4 className={'my-0'}>{name}</h4>
              <span
                style={{ color: darkTheme.palette.info.main, fontSize: 12 }}
              >
                {t(`roles.${userData?.role}`)}
              </span>
            </div>
            <KeyboardArrowDownOutlinedIcon />
          </Box>
          <Menu
            sx={{ mt: '60px' }}
            id="menu-appbar"
            anchorEl={anchorElUser}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
          >
            <MenuItem
              onClick={() => {
                navigate(
                  `/${isNormalUser ? 'user' : 'admin'}/employee/preview/${userData?.id}`,
                );
                setAnchorElUser(null);
              }}
            >
              <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                <Person2OutlinedIcon />
                {t('header.myProfile')}
              </Typography>
            </MenuItem>
            <MenuItem onClick={logoutUser}>
              <Typography
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: 'error.main',
                }}
              >
                <LogoutOutlinedIcon />
                {t('header.logout')}
              </Typography>
            </MenuItem>
          </Menu>
        </Card>
      </div>
    </header>
  );
};

export default Header;
